import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import RootLayout from '../layouts/RootLayout';
import HomePage from '../pages/HomePage';
import LoadingScreen from '../components/LoadingScreen';

// ---------- Public pages (lazy except HomePage which is critical) ----------
const AboutPage = lazy(() => import('../pages/AboutPage'));
const ContactPage = lazy(() => import('../pages/ContactPage'));
const ServicesPage = lazy(() => import('../pages/ServicesPage'));
const ServiceDetailPage = lazy(() => import('../pages/ServiceDetailPage'));
const ServiceProjectsPage = lazy(() => import('../pages/ServiceProjectsPage'));
const PublicProjectsPage = lazy(() => import('../pages/ProjectsPage'));
const PublicProjectDetailsPage = lazy(() => import('../pages/PublicProjectDetailsPage'));
const CareersPage = lazy(() => import('../pages/CareersPage'));
const JobDetailsPage = lazy(() => import('../pages/JobDetailsPage'));
const ApplyJobPage = lazy(() => import('../pages/ApplyJobPage'));
const FAQPage = lazy(() => import('../pages/FAQPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

// ---------- Admin (lazy, never loaded for public visitors) ----------
const AdminLayout = lazy(() => import('../layouts/AdminLayout'));
const ProtectedRoute = lazy(() => import('../components/admin/ProtectedRoute'));
const LoginPage = lazy(() => import('../pages/admin/LoginPage'));
const FavoriteApplicationsPage = lazy(() => import('../pages/admin/FavoriteApplicationsPage'));
const EmployeesPage = lazy(() => import('../pages/admin/EmployeesPage'));
const CreateEmployeePage = lazy(() => import('../pages/admin/CreateEmployeePage'));
const ContactMessagesPage = lazy(() => import('../pages/admin/ContactMessagesPage'));
const ServiceRequestsPage = lazy(() => import('../pages/admin/ServiceRequestsPage'));
const JobApplicationsPage = lazy(() => import('../pages/admin/JobApplicationsPage'));
const JobsPage = lazy(() => import('../pages/admin/JobsPage'));
const CreateJobPage = lazy(() => import('../pages/admin/CreateJobPage'));
const EditJobPage = lazy(() => import('../pages/admin/EditJobPage'));
const AdminProjectsPage = lazy(() => import('../pages/admin/ProjectsPage'));
const CreateProjectPage = lazy(() => import('../pages/admin/CreateProjectPage'));
const EditProjectPage = lazy(() => import('../pages/admin/EditProjectPage'));
const ProjectDetailsPage = lazy(() => import('../pages/admin/ProjectDetailsPage'));
const SettingsPage = lazy(() => import('../pages/admin/SettingsPage'));
const HomePageContent = lazy(() => import('../pages/admin/HomePageContent'));
const AboutPageContent = lazy(() => import('../pages/admin/AboutPageContent'));
const FaqContent = lazy(() => import('../pages/admin/FaqContent'));
const ServicesContent = lazy(() => import('../pages/admin/ServicesContent'));

// Wrap a lazy element in Suspense so each navigation has a fallback.
const withSuspense = (node) => (
  <Suspense fallback={<LoadingScreen />}>{node}</Suspense>
);

export const router = createBrowserRouter([
  // Admin Routes
  {
    path: '/admin/login',
    element: withSuspense(<LoginPage />),
  },
  {
    path: '/admin',
    element: withSuspense(
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="contact-messages" replace /> },
      { path: 'contact-messages', element: withSuspense(<ContactMessagesPage />) },
      { path: 'service-requests', element: withSuspense(<ServiceRequestsPage />) },
      { path: 'job-applications', element: withSuspense(<JobApplicationsPage />) },
      { path: 'jobs', element: withSuspense(<JobsPage />) },
      { path: 'jobs/create', element: withSuspense(<CreateJobPage />) },
      { path: 'jobs/edit/:id', element: withSuspense(<EditJobPage />) },
      { path: 'projects', element: withSuspense(<AdminProjectsPage />) },
      { path: 'projects/create', element: withSuspense(<CreateProjectPage />) },
      { path: 'projects/edit/:id', element: withSuspense(<EditProjectPage />) },
      { path: 'projects/:id', element: withSuspense(<ProjectDetailsPage />) },
      { path: 'favorites', element: withSuspense(<FavoriteApplicationsPage />) },
      { path: 'employees', element: withSuspense(<EmployeesPage />) },
      { path: 'employees/create', element: withSuspense(<CreateEmployeePage />) },
      { path: 'settings', element: withSuspense(<SettingsPage />) },
      { path: 'home-content', element: withSuspense(<HomePageContent />) },
      { path: 'about-content', element: withSuspense(<AboutPageContent />) },
      { path: 'faq-content', element: withSuspense(<FaqContent />) },
      { path: 'services-content', element: withSuspense(<ServicesContent />) },
    ],
  },

  // Public Routes
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'about', element: withSuspense(<AboutPage />) },
      { path: 'contact', element: withSuspense(<ContactPage />) },
      { path: 'services', element: withSuspense(<ServicesPage />) },
      { path: 'services/:serviceId', element: withSuspense(<ServiceDetailPage />) },
      { path: 'services/:serviceId/projects', element: withSuspense(<ServiceProjectsPage />) },
      { path: 'projects', element: withSuspense(<PublicProjectsPage />) },
      { path: 'projects/:projectId', element: withSuspense(<PublicProjectDetailsPage />) },
      { path: 'careers', element: withSuspense(<CareersPage />) },
      { path: 'careers/:id', element: withSuspense(<JobDetailsPage />) },
      { path: 'careers/:id/apply', element: withSuspense(<ApplyJobPage />) },
      { path: 'faq', element: withSuspense(<FAQPage />) },
    ],
  },
  {
    path: '*',
    element: withSuspense(<NotFoundPage />),
  },
]);
